import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import apiClient from "../api/axios";

const useApi = () => {
  // GET
  const get = (url, { onSuccess, onError, onSettled } = {}) => {
    return useQuery({
      queryKey: [url],
      queryFn: async () => {
        const res = await apiClient.get(url);
        return res.data;
      },
      onSuccess: (data, variables, context) => {
        if (typeof onSuccess === "function") {
          onSuccess(data, variables, context);
        }
      },
      onError: (error, variables, context) => {
        if (typeof onError === "function") {
          onError(error, variables, context);
        }
      },
      onSettled: (data, error, variables, context) => {
        if (typeof onSettled === "function") {
          onSettled(data, error, variables, context);
        }
      },
    });
  };

  // GET by Object
  const getbyobj = (url, params, { onSuccess, onError, onSettled } = {}) => {
    return useQuery({
      queryKey: [url, params],
      queryFn: async () => {
        const res = await apiClient.get(url, { params });
        return res.data;
      },
      onSuccess: (data, variables, context) => {
        if (typeof onSuccess === "function") {
          onSuccess(data, variables, context);
        }
      },
      onError: (error, variables, context) => {
        if (typeof onError === "function") {
          onError(error, variables, context);
        }
      },
      onSettled: (data, error, variables, context) => {
        if (typeof onSettled === "function") {
          onSettled(data, error, variables, context);
        }
      },
    });
  };

  // GET by ID
  const getById = (url, id, { onSuccess, onError, onSettled } = {}) => {
    return useQuery({
      queryKey: [url, id],
      queryFn: async () => {
        const res = await apiClient.get(`${url}/${id}`);
        return res.data;
      },
      onSuccess: (data, variables, context) => {
        if (typeof onSuccess === "function") {
          onSuccess(data, variables, context);
        }
      },
      onError: (error, variables, context) => {
        if (typeof onError === "function") {
          onError(error, variables, context);
        }
      },
      onSettled: (data, error, variables, context) => {
        if (typeof onSettled === "function") {
          onSettled(data, error, variables, context);
        }
      },
      enabled: !!id, // don't run if no ID
    });
  };

  // POST
  const post = (url, { onSuccess, onError, onSettled } = {}) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (payload) => {
        const response = await apiClient.post(url, payload);
        return response.data;
      },
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries([url]);
        // Also invalidate list endpoints when updating a specific resource
        if (typeof url === "string" && url.startsWith("/offers")) {
          queryClient.invalidateQueries(["/offers"]);
        }
        if (typeof onSuccess === "function") {
          onSuccess(data, variables, context);
        }
      },
      onError: (error, variables, context) => {
        if (typeof onError === "function") {
          onError(error, variables, context);
        }
      },
      onSettled: (data, error, variables, context) => {
        if (typeof onSettled === "function") {
          onSettled(data, error, variables, context);
        }
      },
    });
  };

  // PUT
  const put = (url, { onSuccess, onError, onSettled } = {}) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (input) => {
        // Support two call styles:
        // 1) mutate({ id, payload }) where base URL expects id to be appended
        // 2) mutate(payload) where the URL already contains the id
        let payload = input;
        let id;
        const isStructured =
          input &&
          typeof input === "object" &&
          !(input instanceof FormData) &&
          (Object.prototype.hasOwnProperty.call(input, "payload") ||
            Object.prototype.hasOwnProperty.call(input, "id"));
        if (isStructured) {
          id = input.id;
          payload = input.payload;
        }
        const fullUrl = id ? url + id : url;
        const response = await apiClient.put(fullUrl, payload);
        return response.data;
      },
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries([url]);
        // Also invalidate list endpoints when updating a specific resource
        if (typeof url === "string" && url.startsWith("/offers")) {
          queryClient.invalidateQueries(["/offers"]);
          queryClient.invalidateQueries(["/offers/"]);
        }
        // Invalidate user queries when updating profile
        if (typeof url === "string" && (url.includes("profile") || url.includes("admin"))) {
          queryClient.invalidateQueries(["user"]);
          queryClient.invalidateQueries(["me"]);
        }
        if (typeof onSuccess === "function") {
          onSuccess(data, variables, context);
        }
      },
      onError: (error, variables, context) => {
        if (typeof onError === "function") {
          onError(error, variables, context);
        }
      },
      onSettled: (data, error, variables, context) => {
        if (typeof onSettled === "function") {
          onSettled(data, error, variables, context);
        }
      },
    });
  };

  // DELETE
  const del = (url, { onSuccess, onError, onSettled } = {}) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (id) => {
        const res = await apiClient.delete(`${url}/${id}`);
        return res.data;
      },
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries([url]);
        if (typeof onSuccess === "function") {
          onSuccess(data, variables, context);
        }
      },
      onError: (error, variables, context) => {
        if (typeof onError === "function") {
          onError(error, variables, context);
        }
      },
      onSettled: (data, error, variables, context) => {
        if (typeof onSettled === "function") {
          onSettled(data, error, variables, context);
        }
      },
    });
  };

  return {
    get,
    getbyobj,
    getById,
    post,
    put,
    del,
  };
};

export default useApi;
